// packages/auth/src/guards/AuthGuard.tsx

import type { ReactNode } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import { isUnsafePostLoginPath } from '../safeRedirect';
import { useAuth } from '../useAuth';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Route guard that redirects to /login if the user is not authenticated.
 * Shows a loading spinner while the initial auth check is in progress.
 *
 * Preserves the intended destination in location.state.from so that
 * after login, the user can be redirected back to the original page.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '4px solid #e5e7eb',
            borderTopColor: '#EE781F',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Quando a rota atual e publica (magic-link cliente, landing de cotacao,
    // etc.) NAO preservamos `from` — caso contrario o post-login redirecionaria
    // o admin/analyst de volta para uma URL publica (ex: /p/<jwt>/pendencias).
    const stateFrom = isUnsafePostLoginPath(location.pathname) ? undefined : location;
    return <Navigate to="/login" state={stateFrom ? { from: stateFrom } : undefined} replace />;
  }

  return <>{children}</>;
}
